import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { getSimUsersByCompany } from '../Services/APIs/dashboard';
import { getClinicDetails } from '../Utils/clinicAuth';
import logo3 from "../assets/claraViewMedia/7534fa44a470f842563a9393f7f382ec6e8d5f7b.avif"
import logo2 from "../assets/claraViewMedia/a83b2b877fc18b05b1e568e9e404862786163ade.png"
import logo1 from "../assets/claraViewMedia/3565cef2412ef1b36571971c03310d43f8b5d114.avif"

const STATUS_COLORS = {
    'New': 'bg-[#C3D8CC] text-[#2F4437]',
    'Contacted': 'bg-[#92A395] text-[#2F3A32]',
    'High Intent': 'bg-[#D3FDD9] text-[#2F5E38]',
    'Consultation Scheduled': 'bg-[#58665A] text-[#D0D9D2]',
};

const CARD_CLASSES = "bg-white rounded-[16px] border border-[#e8e6e3] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]";

const StatusBadge = ({ status }) => (
    <span
        className={`inline-flex items-center justify-center h-5 px-2 rounded-[8px] font-['Aeonik:Medium',sans-serif] text-[12px] leading-none whitespace-nowrap ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}
    >
        {status}
    </span>
);

/* The count pill next to a patient's name — how many scans they've taken. */
const ScanCountBadge = ({ count }) => (
    <div className="group relative shrink-0">
        <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full bg-[#E8F5E8] text-[#2F5E38] font-['Aeonik:Medium',sans-serif] text-[10px] font-medium cursor-help">
            {count}
        </span>
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            Total checks taken
        </div>
    </div>
);

const StatCard = ({ icon, value, label, note, className = '', ...rest }) => (
    <div className={`${CARD_CLASSES} p-4 md:p-6 flex flex-col gap-6 md:gap-12 min-h-[160px] md:min-h-[242px] ${className}`} {...rest}>
        {/* Fixed icon height + top-anchored value: keeps number/label on the same
            line across cards regardless of logo aspect ratio or an extra note line. */}
        <img src={icon} alt="" className="h-7 md:h-[30px] w-auto max-w-[30px] object-contain self-start" />
        <div>
            <p className="font-['Aeonik:Regular',sans-serif] text-[56px] md:text-[80px] lg:text-[100px] leading-none tracking-[-1.2px] text-[#2d2d2d]">
                {value}
            </p>
            <p className="font-dashboard text-[10px] md:text-[13px] lg:text-[18px] uppercase text-[#6b6b6b] mt-1.5 md:mt-2 leading-tight">
                {label}
            </p>
            {note && (
                <p className="font-['Aeonik:Regular',sans-serif] text-[11px] md:text-[12px] text-[#839282] mt-1.5 md:mt-2">
                    {note}
                </p>
            )}
        </div>
    </div>
);

const DashboardContent = ({ refreshKey = 0, onNewSimulation }) => {
    const [stats, setStats] = useState({ new: 0, highIntent: 0, total: 0 });
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [drName] = useState(getClinicDetails()?.doctor_name);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch all users to get stats
                const response = await getSimUsersByCompany({
                    page: 1,
                    limit: 8, // Get more users for accurate stats
                });

                if (response.data) {
                    const { users, counts } = response.data;

                    // Update stats with actual counts
                    setStats({
                        new: counts?.new_users || 0,
                        highIntent: counts?.high_intent_users || 0,
                        total: counts?.total_users || 0,
                    });

                    // Transform and set table data (show all 8 records)
                    const transformedUsers = users.map((user) => ({
                        _id: user._id,
                        uid: user.patient_UUID,
                        patient: `${user.first_name} ${user.last_name}`,
                        email: user.email,
                        date: new Date(user.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                        }),
                        statuses: buildStatuses(user),
                        phone: user.phone,
                        uids: user.uids,
                        uidCount: Array.isArray(user.uids) ? user.uids.length : 0
                    }));

                    setTableData(transformedUsers);
                }
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError(err?.response?.data?.message || 'Failed to fetch dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [refreshKey]);

    const buildStatuses = (user) => {
        const statuses = [];

        if (user.status === 'new') {
            statuses.push('New');
        } else if (user.status === 'contacted') {
            statuses.push('Contacted');
        } else if (user.status === 'consultation_scheduled') {
            statuses.push('Consultation Scheduled');
        }

        if (user.is_high_intent) {
            statuses.push('High Intent');
        }

        return statuses;
    };

    const openPatient = (patient) =>
        navigate(`/dashboard/patients/${patient.uid}`, { state: { patient } });

    // Loading / error / empty are shared by both the table and the mobile list.
    const placeholder = loading ? (
        <div className="flex justify-center items-center py-12 w-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D2D2D]" />
        </div>
    ) : error ? (
        <div className="flex justify-center items-center py-12 w-full">
            <p className="text-sm text-[#6b6b6b]">{error}</p>
        </div>
    ) : tableData.length === 0 ? (
        <div className="flex justify-center items-center py-12 w-full">
            <p className="text-sm text-[#6b6b6b]">No patients found</p>
        </div>
    ) : null;

    return (
        <div className="w-full">
            <div className="flex flex-col gap-5 md:gap-8 px-4 md:px-8 pt-4 md:pt-8 pb-8">

                {/* Welcome banner */}
                <div className="relative rounded-[16px] border border-[#e8e6e3] bg-gradient-to-r from-[#161612] to-[#38433a] min-h-[148px] md:min-h-[209px] flex flex-col justify-end overflow-hidden">
                    <div className="px-5 md:px-6 py-5">
                        <p className="font-['Aeonik:Medium',sans-serif] text-[28px] md:text-[45px] leading-tight md:leading-[52px] text-[#e8e6e3] tracking-[-0.75px]">
                            Welcome back, Dr. {drName}
                        </p>
                    </div>
                </div>

                {/* The rail CTA is hidden below md, so the overview carries its own on phones. */}
                {onNewSimulation && (
                    <button
                        type="button"
                        onClick={onNewSimulation}
                        className="md:hidden w-full h-12 flex items-center justify-center gap-2 bg-[#2D2D2D] hover:bg-[#1A1A1A] active:bg-[#111111] transition-colors rounded-xl text-white"
                    >
                        <Plus className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
                        <span className="font-dashboard text-[11px] uppercase tracking-[0.5px]">New Simulation</span>
                    </button>
                )}

                {/* KPI cards */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                    <StatCard icon={logo1} value={loading ? '-' : stats.new} label="new Smile Previews" />
                    <StatCard
                        icon={logo2}
                        value={loading ? '-' : stats.highIntent}
                        label="high-intent patients"
                        note="[Requested Pricing]"
                        className="cursor-pointer hover:shadow-lg transition-shadow"
                    />
                    <StatCard
                        icon={logo3}
                        value={loading ? '-' : stats.total}
                        label="Total Smile Previews"
                        className="col-span-2 lg:col-span-1"
                    />
                </div>

                {/* Recent previews */}
                <div className={`${CARD_CLASSES} overflow-hidden`}>
                    <div className="border-b border-[#e8e6e3] px-4 md:px-6 py-[18px]">
                        <p className="font-['Aeonik:Medium',sans-serif] text-[18px] leading-7 text-[#2d2d2d]">
                            Recent Smile Previews
                        </p>
                    </div>

                    {placeholder}

                    {!placeholder && (
                        <>
                            {/* Desktop table — scrolls within its own container, never the page */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full min-w-[680px]">
                                    <thead>
                                        <tr className="border-b border-[#e8e6e3]">
                                            {['Patient', 'Email', 'Date', 'Status'].map((h) => (
                                                <th key={h} className="text-left px-6 py-3.5 font-normal">
                                                    <span className="font-dashboard text-[12px] tracking-[0.6px] uppercase text-[#6b6b6b]">
                                                        {h}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.map((patient) => (
                                            <tr
                                                key={patient.uid}
                                                onClick={() => openPatient(patient)}
                                                className="border-b border-[#e8e6e3] last:border-b-0 hover:bg-[#FAFAF9] transition-colors cursor-pointer"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-['Aeonik:Regular',sans-serif] text-[14px] text-[#2d2d2d]">
                                                            {patient.patient}
                                                        </span>
                                                        {patient.uidCount > 1 && <ScanCountBadge count={patient.uidCount} />}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="font-['Aeonik:Regular',sans-serif] text-[14px] text-[#6b6b6b]">
                                                        {patient.email}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="font-['Aeonik:Regular',sans-serif] text-[14px] text-[#6b6b6b]">
                                                        {patient.date}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        {patient.statuses.map((status) => (
                                                            <StatusBadge key={status} status={status} />
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile card list — the table's columns don't fit a phone */}
                            <div className="md:hidden divide-y divide-[#e8e6e3]">
                                {tableData.map((patient) => (
                                    <div
                                        key={patient.uid}
                                        onClick={() => openPatient(patient)}
                                        className="flex items-center justify-between px-4 py-3.5 hover:bg-[#FAFAF9] active:bg-[#f3f3f0] transition-colors cursor-pointer"
                                    >
                                        <div className="flex flex-col gap-1.5 min-w-0">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="font-['Aeonik:Regular',sans-serif] text-[14px] text-[#2d2d2d] truncate">
                                                    {patient.patient}
                                                </span>
                                                {patient.uidCount > 1 && <ScanCountBadge count={patient.uidCount} />}
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {patient.statuses.map((status) => (
                                                    <StatusBadge key={status} status={status} />
                                                ))}
                                            </div>
                                        </div>
                                        <span className="font-dashboard text-[10px] text-[#ADADAD] tracking-[0.3px] shrink-0 ml-3">
                                            {patient.date}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardContent;
